import { Auth0Client, Auth0ClientOptions } from '@auth0/auth0-spa-js';
import get from 'lodash/get';

import { ERROR_STATUS } from '@futrli/errors';

import { Authenticator } from '../authenticator';
import { Auth0Credentials, Auth0User, CurrentUserCheck, UserAttribute } from './types';
import { transformAuth0User, TransformedAuth0User } from './utils';
import { APIClients, ApiClientType } from '../../api-client';
import { Logger } from '../../logger';
import { checkCurrentUser } from '../../users';
import { ROUTES, QUERY_PARAMS } from '../../app-core';

/**
 * Combines the SDK's Auth0 identity with the backend profile expected by the frontend.
 * The SDK remains responsible for authentication; a retained profile does not establish a session.
 */
export class Auth0Authenticator extends Authenticator<Auth0ClientOptions> {
  private auth0Client: Auth0Client;
  // Last confirmed backend profile: Auth0 supplies a Sage ID, but the frontend needs the backend UUID.
  private currentUser: CurrentUserCheck | null;
  // Concurrent readers await this same promise rather than transforming a user before its profile loads.
  private currentUserRequest: Promise<CurrentUserCheck> | null;
  // Session generation, not a profile revision. Resetting invalidates async work from older generations.
  private currentUserVersion = 0;
  // Injected by app setup so this package does not depend on the views/Query layer.
  private currentUserLoader?: () => Promise<CurrentUserCheck>;
  // App setup uses this callback to clear shared cache data on logout or account changes.
  private onIdentityChanged?: () => void;
  // Last SDK user.sub, used to detect account changes independently of the backend UUID.
  private principal?: string;
  // Suppresses further profile loads after a failure until reset or a confirmed profile update.
  private errorCurrentUser: boolean;

  constructor(config: Auth0ClientOptions, client: Auth0Client) {
    super(config);
    this.auth0Client = client;
    this.currentUser = null;
    this.currentUserRequest = null;
    this.errorCurrentUser = false;
  }

  public async fetchCredentials(): Promise<Auth0Credentials | never> {
    const idToken = await this.auth0Client.getIdTokenClaims();
    return { jwtToken: idToken?.__raw || '' };
  }

  public async hasExpiredCredentials(): Promise<boolean | never> {
    const isAuthenticated = await this.auth0Client.isAuthenticated();
    return !isAuthenticated;
  }

  public async signOut(showError?: boolean): Promise<void> {
    // Invalidate pending work and clear shared data before awaiting the provider's logout redirect.
    this.resetCurrentUser();
    this.onIdentityChanged?.();
    await this.auth0Client.logout({
      logoutParams: {
        returnTo: `${window.location.origin}${ROUTES.LOGIN}${showError ? `?${QUERY_PARAMS.SAGE_50_ERROR}=true` : ''}`
      }
    });
  }

  /**
   * Reads the SDK identity and merges it with the backend profile in the frontend's existing user shape.
   * A caller-supplied profile avoids another load. Returns null if no SDK user exists or work is invalidated.
   */
  public async getCurrentAuthenticatedUser(currentUser?: CurrentUserCheck): Promise<TransformedAuth0User | null> {
    // A reset during the SDK lookup must invalidate this attempt, just like a reset during profile loading.
    const version = this.currentUserVersion;
    const user = await this.auth0Client.getUser<Auth0User>();
    if (version !== this.currentUserVersion) {
      return null;
    }
    const currentVersion = this.syncPrincipal(user?.sub);
    this.updateCurrentUser(currentUser);
    if (!user) {
      return null;
    }
    // When configured, the shared loader owns freshness; the retained snapshot must not bypass it.
    const shouldLoad = !currentUser && !this.errorCurrentUser && (this.currentUserLoader || !this.currentUser);
    if (shouldLoad && !await this.loadCurrentUser(currentVersion)) {
      return null;
    }
    return transformAuth0User(user, this.currentUser);
  }

  /**
   * Retains a confirmed backend profile and allows profile loading again after an earlier failure.
   */
  public updateCurrentUser(newCurrentUser?: CurrentUserCheck): void {
    if (newCurrentUser) {
      this.currentUser = newCurrentUser;
      this.errorCurrentUser = false;
    }
  }

  /**
   * Connects profile loading and cache cleanup to the app's shared QueryClient without importing it here.
   * Without an injected loader, profile loading falls back to the direct backend request.
   */
  public configureCurrentUser(loader: () => Promise<CurrentUserCheck>, onIdentityChanged: () => void): void {
    this.currentUserLoader = loader;
    this.onIdentityChanged = onIdentityChanged;
  }

  /**
   * Exposes the retained profile for existing auth/Redux handoffs; does not validate freshness or authentication.
   */
  public getCachedCurrentUser(): CurrentUserCheck | undefined {
    return this.currentUser ?? undefined;
  }

  /**
   * Drops retained identity/profile state and invalidates pending results without canceling their transport.
   * Shared-cache cleanup is separate: logout and detected account changes invoke onIdentityChanged.
   */
  public resetCurrentUser(): void {
    this.currentUserVersion += 1;
    this.currentUser = null;
    this.currentUserRequest = null;
    this.errorCurrentUser = false;
    this.principal = undefined;
  }

  public async getUserAttributes(user: Auth0User): Promise<UserAttribute[]> {
    const { attributes } = user;
    const userAttributes: UserAttribute[] = [];
    Object.keys(attributes).forEach(k => {
      userAttributes.push({ Name: k, Value: attributes[k] });
    });
    return userAttributes;
  }

  public async updateUserAttributes(_user: Auth0User, _attributes: Partial<Auth0User>): Promise<Auth0User> {
    throw new Error('updateUserAttributes is not supported for Auth0 via client-side.');
  }

  public async changePassword(_user: Auth0User, _oldPassword: string, _newPassword: string): Promise<void> {
    throw new Error('changePassword is not supported for Auth0 via client-side.');
  }

  public async completeNewPassword(
    _user: Auth0User,
    _password: string,
    _requiredAttributes: unknown = {}
  ): Promise<void> {
    throw new Error('completeNewPassword is not supported for Auth0.');
  }

  public async forgotPassword(_username: string): Promise<void> {
    throw new Error('forgotPassword is not supported for Auth0 via client-side.');
  }

  public async forgotPasswordSubmit(_username: string, _code: string, _password: string): Promise<void> {
    throw new Error('forgotPasswordSubmit is not supported for Auth0 via client-side.');
  }

  public async signIn(username?: string, _password?: string): Promise<void> {
    await this.auth0Client.loginWithRedirect({
      authorizationParams: {
        redirect_uri: `${window.location.origin}${ROUTES.SAGE_ID_SIGN_IN_TRIGGER}`,
        login_hint: username || ''
      }
    });
  }

  public async handleRedirectCallback(): Promise<void> {
    await this.auth0Client.handleRedirectCallback();
  }

  public async confirmSignIn(_user: Auth0User, _code: string, _mfaType: string | null): Promise<void> {
    throw new Error('confirmSignIn is not supported for Auth0.');
  }

  public async getPreferredMFA(_user: Auth0User): Promise<string> {
    throw new Error('getPreferredMFA is not supported for Auth0.');
  }

  public async setPreferredMFA(_user: Auth0User, _mfaMethod: string): Promise<void> {
    throw new Error('setPreferredMFA is not supported for Auth0.');
  }

  public async setupTOTP(_user: Auth0User): Promise<void> {
    throw new Error('setupTOTP is not supported for Auth0.');
  }

  public async verifyTotpToken(_user: Auth0User, _challengeAnswer: unknown): Promise<void> {
    throw new Error('verifyTotpToken is not supported for Auth0.');
  }

  public async verifyCurrentUserAttributeSubmit(_attribute: string, _code: string): Promise<void> {
    throw new Error('verifyCurrentUserAttributeSubmit is not supported for Auth0.');
  }

  public async verifyUserAttribute(_user: Auth0User, _attrs: string): Promise<void> {
    throw new Error('verifyUserAttribute is not supported for Auth0.');
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-unsafe-function-type, @typescript-eslint/no-wrapper-object-types
  public async signUp(_data: string | object, ..._restOfAttrs: string[]): Promise<void> {
    throw new Error('signUp is not supported for Auth0 via client-side.');
  }

  public async confirmSignUp(_username: string, _code: string, _options?: unknown): Promise<void> {
    throw new Error('confirmSignUp is not supported for Auth0.');
  }

  public async federatedSignIn(_options?: unknown): Promise<void> {
    throw new Error('federatedSignIn is not supported for Auth0.');
  }

  public async resendSignUp(_username: string): Promise<void> {
    throw new Error('resendSignUp is not supported for Auth0.');
  }

  /**
   * Shares one profile request and accepts its result only for the supplied session generation.
   * False stops authentication completion; true also covers the existing non-403 profile-error fallback.
   */
  private async loadCurrentUser(version: number): Promise<boolean> {
    try {
      APIClients.setApiClient(ApiClientType.Auth0);
      this.currentUserRequest ??= this.currentUserLoader ? this.currentUserLoader() : checkCurrentUser();
      const currentUser = await this.currentUserRequest;
      if (version !== this.currentUserVersion) {
        return false;
      }
      this.currentUser = currentUser;
      return true;
    } catch (error) {
      if (version !== this.currentUserVersion) {
        return false;
      }
      if (get(error, 'response.status') === ERROR_STATUS._403) {
        Logger.error(
          "Can't set current user as currentUser route is returning 403 error -> user's session has been terminated"
        );
        this.signOut();
        return false;
      }
      // Preserve the existing provisioning fallback for non-403 errors; the log assumes a missing user.
      this.errorCurrentUser = true;
      Logger.error(
        "Can't set current user as currentUser route is returning 404 error -> Newly signed up user didn't submit provision request yet"
      );
      return true;
    } finally {
      // An old request must not clear the promise belonging to a replacement session.
      if (version === this.currentUserVersion) {
        this.currentUserRequest = null;
      }
    }
  }

  /**
   * Resets profile/cache state when switching between known SDK identities and returns the active generation.
   * The first observed identity simply establishes the baseline.
   */
  private syncPrincipal(principal?: string): number {
    if (principal && this.principal && principal !== this.principal) {
      this.resetCurrentUser();
      this.onIdentityChanged?.();
    }
    this.principal = principal;
    return this.currentUserVersion;
  }
}
